// SPDX-License-Identifier: GPL-3.0-or-later
// Builds the three.js scene of a table from a TableSpec. This file needs no WebGL context.
// Every part is a code-built mesh (see src/render/meshes). The scene frame is the three.js
// frame of coords.ts: x right, y up, z toward the player (table y).

import {
  Box3,
  type BufferGeometry,
  CanvasTexture,
  CatmullRomCurve3,
  Color,
  Group,
  LineBasicMaterial,
  LineLoop,
  type Material,
  Matrix4,
  Mesh,
  MeshBasicMaterial,
  type MeshPhysicalMaterial,
  MeshStandardMaterial,
  type Object3D,
  PlaneGeometry,
  Scene,
  ShadowMaterial,
  SRGBColorSpace,
  type Texture,
  TubeGeometry,
  Vector3,
} from "three";
import type {
  BallFrame,
  DecorationFactory,
  DecorationSpec,
  ItemKind,
  LightSpec,
  TableSpec,
} from "../shared/contracts.ts";
import type { PlayfieldBounds } from "./camera.ts";
import { type CanvasLike, type CreateCanvas, defaultCreateCanvas, drawLabel } from "./canvas.ts";
import { meshToGeometry } from "./convert.ts";
import { placeAt, rad, surfaceHeight, tableHeight } from "./coords.ts";
import { createFloorZones } from "./floor.ts";
import { createBulbLight, createSceneLights, InsertState } from "./lights.ts";
import { BRAND, MaterialLibrary } from "./materials.ts";
import { generateBall } from "./meshes/ball.ts";
import { generateBumper } from "./meshes/bumper.ts";
import { generateFlipper } from "./meshes/flipper.ts";
import { generateGate, isPlateGate } from "./meshes/gate.ts";
import { generateKicker, isKickerVisible } from "./meshes/kicker.ts";
import { generateLight } from "./meshes/light.ts";
import { generatePlayfieldGeometry } from "./meshes/playfield.ts";
import { generatePlungerRod, plungerRestY } from "./meshes/plunger.ts";
import { generateWireRamp } from "./meshes/ramp.ts";
import { generateRubber } from "./meshes/rubber.ts";
import { generateSpinner } from "./meshes/spinner.ts";
import { generateTarget } from "./meshes/target.ts";
import { generateTrigger } from "./meshes/trigger.ts";
import { generateWallMeshes } from "./meshes/wall.ts";
import {
  DROP_TARGET_LIMIT,
  flipperUpdater,
  type ItemUpdater,
  offsetUpdater,
  pivotUpdater,
  plungerUpdater,
  targetUpdater,
  visibleUpdater,
} from "./updaters.ts";
import { colorOf, lampHex } from "./vfx/palette.ts";
import {
  bumperLook,
  flipperEdgeLook,
  gateLook,
  guideLookOf,
  kickerLook,
  type PartLook,
  rampLook,
  rubberLook,
  spinnerLook,
  targetLook,
  triggerLook,
  wallLook,
} from "./vfx/parts.ts";
import { FLOOR_ZONES } from "./vfx/zones.ts";

/** One item in the scene. */
export interface ItemNode {
  readonly kind: ItemKind;
  readonly object: Object3D;
  readonly update: ItemUpdater;
}

/** A decoration factory that can also free the object that it built. */
export interface RenderDecoration extends DecorationFactory<Object3D> {
  /** Free the geometry, the materials and the textures of a built object. */
  dispose?(obj: Object3D): void;
}

/** One decoration in the scene. */
export interface DecorationNode {
  readonly spec: DecorationSpec;
  readonly factory: RenderDecoration;
  readonly object: Object3D;
}

/** The scene of one table and the handles that the renderer needs. */
export interface SceneModel {
  readonly scene: Scene;
  readonly bounds: PlayfieldBounds;
  /**
   * More points that the camera must show, besides the playfield box: the corners of the box
   * of each decoration. A tall character, such as the backboard villain, stands out of the
   * playfield box.
   */
  readonly viewPoints: readonly Vector3[];
  readonly materials: MaterialLibrary;
  readonly items: Map<string, ItemNode>;
  readonly inserts: Map<string, InsertState>;
  readonly decorations: DecorationNode[];
  /** Registry ids that the table names but the registry does not have. */
  readonly missingDecorations: string[];
  /** The group that holds the ball meshes. */
  readonly balls: Group;
  /** Make a ball mesh. The balls share one geometry and one material. */
  createBall(): Mesh;
  /** Free every geometry, material and texture that this scene owns. */
  dispose(): void;
}

/** Inputs of `buildScene`. */
export interface SceneOptions {
  readonly decorations: ReadonlyMap<string, RenderDecoration>;
  readonly createCanvas?: CreateCanvas;
}

/**
 * Height of a label canvas in pixels. The inserts of the table are about 44 units wide, and the
 * camera is close enough that 128 pixels were soft. A square label is this wide. A wider label
 * has the aspect of its insert.
 */
const LABEL_PIXELS = 256;

/** Context of one build. */
interface Ctx {
  readonly table: TableSpec;
  readonly materials: MaterialLibrary;
  readonly createCanvas: CreateCanvas;
  readonly decorations: ReadonlyMap<string, RenderDecoration>;
  readonly ownedMaterials: Material[];
  readonly ownedTextures: Texture[];
  /** Glowing copies of the table materials, by name and look (see `glowOf`). */
  readonly glows: Map<string, Material>;
  readonly root: Group;
  readonly items: Map<string, ItemNode>;
  readonly inserts: Map<string, InsertState>;
  readonly decorationNodes: DecorationNode[];
  readonly missing: string[];
}

/** Free the geometry, the materials and the textures of an object tree. */
export function disposeObject(root: Object3D): void {
  root.traverse((obj) => {
    const holder = obj as Object3D & {
      geometry?: BufferGeometry;
      material?: Material | Material[];
    };
    holder.geometry?.dispose();
    const materials = Array.isArray(holder.material)
      ? holder.material
      : holder.material
        ? [holder.material]
        : [];
    for (const material of materials) {
      for (const value of Object.values(material)) {
        if (value && typeof value === "object" && (value as Texture).isTexture === true)
          (value as Texture).dispose();
      }
      material.dispose();
    }
  });
}

function setShadows(obj: Object3D, cast: boolean, receive: boolean): void {
  obj.traverse((o) => {
    if ((o as Mesh).isMesh === true) {
      o.castShadow = cast;
      o.receiveShadow = receive;
    }
  });
}

function addMesh(
  parent: Object3D,
  geometry: BufferGeometry,
  material: Material,
  name: string,
  opts: { cast?: boolean; receive?: boolean } = {},
): Mesh {
  const mesh = new Mesh(geometry, material);
  mesh.name = name;
  mesh.castShadow = opts.cast ?? true;
  mesh.receiveShadow = opts.receive ?? true;
  parent.add(mesh);
  return mesh;
}

function ownMaterial<T extends Material>(ctx: Ctx, material: T): T {
  ctx.ownedMaterials.push(material);
  return material;
}

/**
 * A copy of a table material with the look of a part: it glows in the colour of the part, and a
 * painted look also takes the colour as its base. The copy is shared by name and look. With no
 * look, the table material comes back as it is.
 */
function glowOf(
  ctx: Ctx,
  base: MeshPhysicalMaterial,
  look: PartLook | null | undefined,
): MeshPhysicalMaterial {
  if (!look) return base;
  const key = `${base.name}|${look.hex}|${look.glow}|${look.paint}`;
  const cached = ctx.glows.get(key) as MeshPhysicalMaterial | undefined;
  if (cached) return cached;
  const material = ownMaterial(ctx, base.clone());
  material.name = key;
  material.emissive.copy(colorOf(look.hex));
  material.emissiveIntensity = look.glow;
  if (look.paint) material.color.copy(colorOf(look.hex));
  ctx.glows.set(key, material);
  return material;
}

/** A neon tube material: a light metal that glows in the colour of the look. Shared by look. */
function neonOf(ctx: Ctx, look: PartLook, base: number): MeshStandardMaterial {
  const key = `neon|${look.hex}|${look.glow}|${base}`;
  const cached = ctx.glows.get(key) as MeshStandardMaterial | undefined;
  if (cached) return cached;
  const material = ownMaterial(
    ctx,
    new MeshStandardMaterial({
      color: base,
      metalness: 0.3,
      roughness: 0.35,
      emissive: colorOf(look.hex).clone(),
      emissiveIntensity: look.glow,
    }),
  );
  material.name = key;
  ctx.glows.set(key, material);
  return material;
}

/** A canvas texture with a label. Returns null when the platform has no canvas. */
function labelTexture(ctx: Ctx, text: string, aspect = 1): CanvasTexture | null {
  const width = Math.max(LABEL_PIXELS, Math.round(LABEL_PIXELS * aspect));
  const canvas: CanvasLike | null = ctx.createCanvas(width, LABEL_PIXELS);
  const g = canvas?.getContext("2d");
  if (!canvas || !g) return null;
  drawLabel(g, width, text, LABEL_PIXELS);
  const texture = new CanvasTexture(canvas as unknown as HTMLCanvasElement);
  texture.colorSpace = SRGBColorSpace;
  ctx.ownedTextures.push(texture);
  return texture;
}

function labelMaterial(ctx: Ctx, text: string, aspect = 1): MeshBasicMaterial | null {
  const texture = labelTexture(ctx, text, aspect);
  if (!texture) return null;
  return ownMaterial(
    ctx,
    new MeshBasicMaterial({
      map: texture,
      transparent: true,
      depthWrite: false,
    }),
  );
}

function register(
  ctx: Ctx,
  name: string,
  kind: ItemKind,
  object: Object3D,
  update: ItemUpdater,
): void {
  ctx.items.set(name, { kind, object, update });
}

// ───────────────────────────── builders ─────────────────────────────

function buildPlayfield(ctx: Ctx): void {
  const { table, materials, root } = ctx;
  const info = table.info;
  const group = new Group();
  group.name = "playfield";
  const slab = addMesh(
    group,
    generatePlayfieldGeometry(info),
    materials.get(info.szPlayfieldMaterial, BRAND.field),
    "playfield-slab",
    { cast: false, receive: true },
  );
  slab.receiveShadow = true;

  // The art is a decoration. It is built in the three.js frame by its factory.
  const artId = info.playfieldArt;
  if (artId !== undefined) {
    const factory = ctx.decorations.get(artId);
    if (factory) {
      const art = factory.build();
      art.name = `playfield-art-${artId}`;
      group.add(art);
    } else {
      ctx.missing.push(artId);
    }
  }

  // Soft colour zones on the floor. They add light under the art text and keep its contrast.
  group.add(createFloorZones(FLOOR_ZONES, tableHeight(table)));

  // A clear plane above the art. It shows the shadows and nothing else.
  const plane = new PlaneGeometry(info.right - info.left, info.bottom - info.top);
  plane.rotateX(-Math.PI / 2);
  const shadow = new Mesh(
    plane,
    ownMaterial(ctx, new ShadowMaterial({ opacity: 0.4, depthWrite: false })),
  );
  shadow.position.set(
    (info.left + info.right) / 2,
    tableHeight(table) + 0.3,
    (info.top + info.bottom) / 2,
  );
  shadow.name = "playfield-shadow";
  shadow.receiveShadow = true;
  shadow.castShadow = false;
  group.add(shadow);
  root.add(group);
}

function buildWalls(ctx: Ctx): void {
  const { table, materials, root } = ctx;
  for (const spec of table.walls) {
    const group = new Group();
    group.name = `wall-${spec.name}`;
    const meshes = generateWallMeshes(spec, tableHeight(table));
    // A wall with a look glows in the colour of its part (parts.ts). Other walls stay gray.
    const look = wallLook(spec.name);
    if (meshes.top && spec.isTopBottomVisible !== false) {
      addMesh(
        group,
        meshToGeometry(meshes.top),
        glowOf(ctx, materials.get(spec.szTopMaterial, 0x444444), look),
        `wall-top-${spec.name}`,
      );
    }
    if (meshes.side && spec.isSideVisible !== false) {
      addMesh(
        group,
        meshToGeometry(meshes.side),
        glowOf(ctx, materials.get(spec.szSideMaterial, 0x666666), look),
        `wall-side-${spec.name}`,
      );
    }
    group.visible = spec.isVisible !== false;
    root.add(group);
    register(ctx, spec.name, "wall", group, visibleUpdater(group));
  }
}

function buildRubbers(ctx: Ctx): void {
  const { table, materials, root } = ctx;
  for (const spec of table.rubbers) {
    const built = generateRubber(spec);
    if (!built) continue;
    const group = new Group();
    group.name = `rubber-${spec.name}`;
    addMesh(
      group,
      built.geometry,
      glowOf(ctx, materials.get(spec.szMaterial, 0x222222), rubberLook()),
      `rubber-mesh-${spec.name}`,
    );
    placeAt(group, built.center.x, built.center.y, tableHeight(table) + spec.height);
    group.rotation.y = -rad(spec.rotZ ?? 0);
    group.visible = spec.isVisible !== false;
    root.add(group);
    register(ctx, spec.name, "rubber", group, visibleUpdater(group));
  }
}

/**
 * Every ramp is drawn as a wire ramp (THEME.md): thin run rails and thin side guides. The player
 * sees the art through them. The collision mesh does not use this geometry.
 */
function buildRamps(ctx: Ctx): void {
  const { table, root } = ctx;
  for (const spec of table.ramps) {
    // Each ramp is a neon tube in one colour (parts.ts). The guides glow less than the run rails.
    const run = rampLook(spec.name);
    const runMaterial = neonOf(ctx, run, 0xcccccc);
    const guideMaterial = neonOf(ctx, guideLookOf(run), BRAND.edge);
    const group = new Group();
    group.name = `ramp-${spec.name}`;
    for (const rail of generateWireRamp(spec, tableHeight(table))) {
      if (rail.points.length < 2) continue;
      const curve = new CatmullRomCurve3(
        rail.points.map((p) => new Vector3(p.x, p.z, p.y)),
        false,
        "centripetal",
      );
      const geometry = new TubeGeometry(
        curve,
        Math.max(16, rail.points.length * 2),
        rail.radius,
        6,
        false,
      );
      geometry.name = rail.name;
      addMesh(group, geometry, rail.role === "guide" ? guideMaterial : runMaterial, rail.name, {
        receive: false,
      });
    }
    group.visible = spec.isVisible !== false;
    root.add(group);
    register(ctx, spec.name, "ramp", group, visibleUpdater(group));
  }
}

function buildFlippers(ctx: Ctx): void {
  const { table, materials, root } = ctx;
  for (const spec of table.flippers) {
    const geometry = generateFlipper(spec);
    const group = new Group();
    group.name = `flipper-${spec.name}`;
    addMesh(
      group,
      geometry.body,
      materials.get(spec.szMaterial, 0xcccccc),
      `flipper-body-${spec.name}`,
    );
    // The bat stays white. Its edge (the rubber) has a colour.
    addMesh(
      group,
      geometry.rubber,
      glowOf(ctx, materials.get(spec.szRubberMaterial, 0x222222), flipperEdgeLook(spec.name)),
      `flipper-rubber-${spec.name}`,
    );
    placeAt(group, spec.center.x, spec.center.y, surfaceHeight(table, spec.szSurface));
    group.rotation.y = -rad(spec.startAngle);
    group.visible = spec.isVisible !== false;
    root.add(group);
    register(ctx, spec.name, "flipper", group, flipperUpdater(group, spec));
  }
}

function buildBumpers(ctx: Ctx): void {
  const { table, materials, root } = ctx;
  for (const [index, spec] of table.bumpers.entries()) {
    const geometry = generateBumper(spec);
    // Each bumper has its own cap colour (parts.ts). The ring glows in the same colour.
    const cap = bumperLook(index);
    const group = new Group();
    group.name = `bumper-${spec.name}`;
    addMesh(
      group,
      geometry.base,
      materials.get(spec.szBaseMaterial, 0x333333),
      `bumper-base-${spec.name}`,
    );
    addMesh(
      group,
      geometry.skirt,
      materials.get(spec.szSkirtMaterial, 0x444444),
      `bumper-socket-${spec.name}`,
    );
    const ringMaterial = glowOf(ctx, materials.get(spec.szRingMaterial, 0xcccccc), {
      hex: cap.hex,
      glow: 0.7,
      paint: false,
    });
    const ring = addMesh(group, geometry.ring, ringMaterial, `bumper-ring-${spec.name}`);
    addMesh(
      group,
      geometry.cap,
      glowOf(ctx, materials.get(spec.szCapMaterial, 0x888888), cap),
      `bumper-cap-${spec.name}`,
    );
    if (spec.capDecoration !== undefined) {
      const factory = ctx.decorations.get(spec.capDecoration);
      if (factory) {
        const object = factory.build();
        object.position.y = geometry.capTop;
        object.name = `bumper-decoration-${spec.name}`;
        group.add(object);
        ctx.decorationNodes.push({
          spec: {
            name: `${spec.name}.cap`,
            meshId: spec.capDecoration,
            position: { x: 0, y: 0, z: 0 },
          },
          factory,
          object,
        });
      } else {
        ctx.missing.push(spec.capDecoration);
      }
    }
    placeAt(group, spec.center.x, spec.center.y, surfaceHeight(table, spec.szSurface));
    group.rotation.y = -rad(spec.orientation ?? 0);
    group.visible = spec.isVisible !== false;
    root.add(group);
    // The ring moves down on a hit. Its rest height is 0 in the group frame.
    register(ctx, spec.name, "bumper", group, offsetUpdater(group, 0, ring));
  }
}

function buildKickers(ctx: Ctx): void {
  const { table, materials, root } = ctx;
  for (const spec of table.kickers) {
    const group = new Group();
    group.name = `kicker-${spec.name}`;
    if (isKickerVisible(spec)) {
      const geometry = generateKicker(spec);
      addMesh(
        group,
        geometry.hole,
        ownMaterial(ctx, new MeshStandardMaterial({ color: 0x050505, roughness: 0.9 })),
        `kicker-hole-${spec.name}`,
        {
          cast: false,
        },
      );
      addMesh(
        group,
        geometry.rim,
        neonOf(ctx, kickerLook(spec.name), BRAND.edge),
        `kicker-rim-${spec.name}`,
        {
          cast: false,
        },
      );
      if (geometry.wall) {
        addMesh(
          group,
          geometry.wall,
          materials.getDoubleSided(spec.szMaterial, 0x666666),
          `kicker-wall-${spec.name}`,
        );
      }
    }
    placeAt(group, spec.center.x, spec.center.y, surfaceHeight(table, spec.szSurface));
    group.rotation.y = -rad(spec.orientation ?? 0);
    group.visible = spec.isVisible !== false;
    root.add(group);
    register(ctx, spec.name, "kicker", group, visibleUpdater(group));
  }
}

function buildTriggers(ctx: Ctx): void {
  const { table, materials, root } = ctx;
  for (const spec of table.triggers) {
    const group = new Group();
    group.name = `trigger-${spec.name}`;
    const geometry = generateTrigger(spec);
    if (geometry)
      addMesh(
        group,
        geometry,
        glowOf(ctx, materials.get(spec.szMaterial, 0xcccccc), triggerLook(spec.name)),
        `trigger-mesh-${spec.name}`,
        { cast: false },
      );
    const baseY = surfaceHeight(table, spec.szSurface);
    placeAt(group, spec.center.x, spec.center.y, baseY);
    group.rotation.y = -rad(spec.rotation ?? 0);
    group.visible = spec.isVisible !== false;
    root.add(group);
    register(ctx, spec.name, "trigger", group, offsetUpdater(group, baseY));
  }
}

function buildGates(ctx: Ctx): void {
  const { table, materials, root } = ctx;
  for (const spec of table.gates) {
    const geometry = generateGate(spec);
    const group = new Group();
    group.name = `gate-${spec.name}`;
    const posts = new Group();
    posts.name = `gate.bracket-${spec.name}`;
    const look = gateLook(spec.name);
    const postMaterial = glowOf(ctx, materials.get(spec.szMaterial, 0xcccccc), look);
    for (const sign of [-1, 1]) {
      const post = addMesh(posts, geometry.post, postMaterial, `gate-post-${spec.name}`);
      post.position.x = sign * (spec.length / 2);
    }
    posts.visible = spec.showBracket !== false;
    group.add(posts);
    const pivot = new Group();
    pivot.name = `gate.wire-${spec.name}`;
    pivot.position.y = geometry.pivotHeight;
    const leafMaterial = isPlateGate(spec)
      ? glowOf(ctx, materials.getDoubleSided(spec.szMaterial, 0xcccccc), look)
      : postMaterial;
    addMesh(pivot, geometry.leaf, leafMaterial, `gate-leaf-${spec.name}`);
    group.add(pivot);
    placeAt(group, spec.center.x, spec.center.y, surfaceHeight(table, spec.szSurface));
    group.rotation.y = -rad(spec.rotation);
    group.visible = spec.isVisible !== false;
    root.add(group);
    const angleMin = spec.angleMin ?? 0;
    pivot.rotation.x = 0;
    register(ctx, spec.name, "gate", group, pivotUpdater(group, pivot, angleMin));
  }
}

function buildSpinners(ctx: Ctx): void {
  const { table, materials, root } = ctx;
  for (const spec of table.spinners) {
    const geometry = generateSpinner(spec);
    const group = new Group();
    group.name = `spinner-${spec.name}`;
    const posts = new Group();
    posts.name = `spinner.bracket-${spec.name}`;
    const material = materials.get(spec.szMaterial, 0xcccccc);
    for (const sign of [-1, 1]) {
      const post = addMesh(posts, geometry.post, material, `spinner-post-${spec.name}`);
      post.position.x = sign * (spec.length / 2);
    }
    posts.visible = spec.showBracket !== false;
    group.add(posts);
    const pivot = new Group();
    pivot.name = `spinner.plate-${spec.name}`;
    pivot.position.y = geometry.pivotHeight;
    addMesh(
      pivot,
      geometry.plate,
      glowOf(ctx, materials.getDoubleSided(spec.szMaterial, 0xdddddd), spinnerLook()),
      `spinner-plate-${spec.name}`,
    );
    group.add(pivot);
    placeAt(group, spec.center.x, spec.center.y, surfaceHeight(table, spec.szSurface));
    group.rotation.y = -rad(spec.rotation);
    group.visible = spec.isVisible !== false;
    root.add(group);
    register(ctx, spec.name, "spinner", group, pivotUpdater(group, pivot, rad(spec.angleMin ?? 0)));
  }
}

function buildPlunger(ctx: Ctx): void {
  const { table, materials, root } = ctx;
  const spec = table.plunger;
  const rod = generatePlungerRod(spec, plungerRestY(spec), surfaceHeight(table, spec.szSurface));
  const geometry = meshToGeometry(rod);
  const mesh = addMesh(
    root,
    geometry,
    materials.get(spec.szMaterial, 0xaaaaaa),
    `plunger-rod-${spec.name}`,
  );
  mesh.frustumCulled = false;
  mesh.visible = spec.isVisible !== false;
  register(ctx, spec.name, "plunger", mesh, plungerUpdater(mesh, geometry, spec));
}

function buildTargets(ctx: Ctx): void {
  const { table, materials, root } = ctx;
  for (const spec of table.targets) {
    const geometry = generateTarget(spec);
    const group = new Group();
    group.name = `target-${spec.name}`;
    addMesh(
      group,
      geometry.body,
      glowOf(ctx, materials.get(spec.szMaterial, 0xdddddd), targetLook(spec.name)),
      `target-body-${spec.name}`,
    );
    if (geometry.label) {
      const material = labelMaterial(ctx, spec.label ?? "");
      if (material)
        addMesh(group, geometry.label, material, `target-label-${spec.name}`, {
          cast: false,
          receive: false,
        });
    }
    const baseY = surfaceHeight(table, spec.szSurface) + spec.position.z;
    placeAt(group, spec.position.x, spec.position.y, baseY);
    group.rotation.order = "YXZ";
    if (spec.isDropped === true && geometry.isDrop) group.position.y = baseY - DROP_TARGET_LIMIT;
    group.rotation.y = -rad(spec.rotZ ?? 0);
    group.visible = spec.isVisible !== false;
    root.add(group);
    register(ctx, spec.name, "target", group, targetUpdater(group, baseY));
  }
}

/** A material of a light insert. It glows with the color of the light. */
function glowMaterial(
  ctx: Ctx,
  spec: { readonly color: number },
  color: number,
  roughness: number,
): MeshStandardMaterial {
  return ownMaterial(
    ctx,
    new MeshStandardMaterial({
      color,
      roughness,
      emissive: new Color(spec.color),
      emissiveIntensity: 0,
    }),
  );
}

/** Add the label of a light insert to its group. */
function addInsertLabel(
  ctx: Ctx,
  group: Group,
  spec: LightSpec,
  geometry: ReturnType<typeof generateLight>,
): void {
  if (!geometry.label) return;
  const material = labelMaterial(ctx, spec.label ?? "", geometry.labelAspect);
  if (material)
    addMesh(group, geometry.label, material, `light-label-${spec.name}`, {
      cast: false,
      receive: false,
    });
}

function buildLights(ctx: Ctx): void {
  const { table, root } = ctx;
  for (const raw of table.lights) {
    // The colour of a lamp follows its family and mode (palette.ts), not the table data.
    const spec: LightSpec = { ...raw, color: lampHex(raw.name, raw.color) };
    const geometry = generateLight(spec);
    const group = new Group();
    group.name = `light-${spec.name}`;
    const fill = glowMaterial(ctx, spec, 0x0a0a0a, 0.6);
    addMesh(group, geometry.fill, fill, `light-fill-${spec.name}`, {
      cast: false,
    });
    const lineMaterial = ownMaterial(ctx, new LineBasicMaterial({ color: BRAND.edge }));
    const outline = new LineLoop(geometry.outline, lineMaterial);
    outline.name = `light-outline-${spec.name}`;
    group.add(outline);
    addInsertLabel(ctx, group, spec, geometry);
    const bulb = geometry.bulb ? glowMaterial(ctx, spec, 0x222222, 0.3) : null;
    if (geometry.bulb && bulb)
      addMesh(group, geometry.bulb, bulb, `light-bulb-${spec.name}`, {
        cast: false,
      });
    let point = null;
    if (spec.bulbLight === true) {
      point = createBulbLight(spec);
      point.position.y = geometry.bulbHeight;
      group.add(point);
    }
    placeAt(group, spec.center.x, spec.center.y, surfaceHeight(table, spec.szSurface));
    group.visible = spec.isVisible !== false;
    root.add(group);
    const insert = new InsertState(spec, fill, bulb, point);
    ctx.inserts.set(spec.name, insert);
    register(ctx, spec.name, "light", group, (frame) => {
      if (frame.visible !== undefined) group.visible = frame.visible;
      if (frame.intensity !== undefined && Number.isFinite(frame.intensity))
        insert.override = frame.intensity;
    });
    insert.apply(0, false);
  }
}

function buildDecorations(ctx: Ctx): void {
  for (const spec of ctx.table.decorations) {
    const factory = ctx.decorations.get(spec.meshId);
    if (!factory) {
      ctx.missing.push(spec.meshId);
      continue;
    }
    const object = factory.build();
    object.name = `decoration-${spec.name}`;
    placeAt(object, spec.position.x, spec.position.y, spec.position.z);
    // The rotation is in three.js axes (y is up), in degrees, XYZ order.
    if (spec.rotation)
      object.rotation.set(rad(spec.rotation.x), rad(spec.rotation.y), rad(spec.rotation.z));
    if (spec.scale) object.scale.set(spec.scale.x, spec.scale.y, spec.scale.z);
    object.visible = spec.isVisible !== false;
    setShadows(object, true, false);
    ctx.root.add(object);
    ctx.decorationNodes.push({ spec, factory, object });
    register(ctx, spec.name, "decoration", object, visibleUpdater(object));
  }
}

/**
 * The corners of the box of each decoration, in the three.js frame. The camera must show them
 * with the playfield box: a tall character stands out of the playfield box.
 */
export function viewPointsOf(decorations: readonly DecorationNode[]): Vector3[] {
  const points: Vector3[] = [];
  for (const node of decorations) {
    const box = new Box3().expandByObject(node.object);
    if (box.isEmpty()) continue;
    for (const x of [box.min.x, box.max.x]) {
      for (const y of [box.min.y, box.max.y]) {
        for (const z of [box.min.z, box.max.z]) points.push(new Vector3(x, y, z));
      }
    }
  }
  return points;
}

/** Build the scene of a table. */
export function buildScene(table: TableSpec, options: SceneOptions): SceneModel {
  const info = table.info;
  const scene = new Scene();
  scene.name = `table-${table.id}`;
  scene.background = new Color(BRAND.field);

  const bounds: PlayfieldBounds = {
    left: info.left,
    top: info.top,
    right: info.right,
    bottom: info.bottom,
    height: info.glassHeight,
  };
  const root = new Group();
  root.name = "table";
  scene.add(root);
  const lights = createSceneLights(bounds);
  scene.add(lights.group);

  const ctx: Ctx = {
    table,
    materials: new MaterialLibrary(table.materials),
    createCanvas: options.createCanvas ?? defaultCreateCanvas,
    decorations: options.decorations,
    ownedMaterials: [],
    ownedTextures: [],
    glows: new Map(),
    root,
    items: new Map(),
    inserts: new Map(),
    decorationNodes: [],
    missing: [],
  };

  buildPlayfield(ctx);
  buildWalls(ctx);
  buildRubbers(ctx);
  buildRamps(ctx);
  buildFlippers(ctx);
  buildBumpers(ctx);
  buildKickers(ctx);
  buildTriggers(ctx);
  buildGates(ctx);
  buildSpinners(ctx);
  buildPlunger(ctx);
  buildTargets(ctx);
  buildLights(ctx);
  buildDecorations(ctx);

  const balls = new Group();
  balls.name = "balls";
  scene.add(balls);
  const ballGeometry = generateBall(info.ballRadius);
  const ballMaterial = new MeshStandardMaterial({
    color: 0xe6e6e6,
    metalness: 0.85,
    roughness: 0.18,
  });
  ballMaterial.name = "ball";
  ctx.ownedMaterials.push(ballMaterial);

  return {
    scene,
    bounds,
    viewPoints: viewPointsOf(ctx.decorationNodes),
    materials: ctx.materials,
    items: ctx.items,
    inserts: ctx.inserts,
    decorations: ctx.decorationNodes,
    missingDecorations: ctx.missing,
    balls,
    createBall: () => {
      const mesh = new Mesh(ballGeometry, ballMaterial);
      mesh.name = "ball";
      mesh.castShadow = true;
      mesh.receiveShadow = false;
      return mesh;
    },
    dispose: () => {
      disposeObject(scene);
      ballGeometry.dispose();
      for (const material of ctx.ownedMaterials) material.dispose();
      for (const texture of ctx.ownedTextures) texture.dispose();
      ctx.materials.dispose();
      scene.clear();
    },
  };
}

const scratchMatrix = new Matrix4();

/** Put a ball mesh at a ball frame. The orientation is a row-major 3x3 matrix in the table frame. */
export function applyBallFrame(mesh: Mesh, ball: BallFrame): void {
  placeAt(mesh, ball.pos.x, ball.pos.y, ball.pos.z);
  const m = ball.orientation;
  if (m.length === 9 && m.every((n) => Number.isFinite(n))) {
    // Swap y and z on both sides of the matrix: R' = P R P. This keeps the rotation valid.
    const p = [0, 2, 1] as const;
    const at = (i: number, j: number): number =>
      m[(p[i] as number) * 3 + (p[j] as number)] as number;
    scratchMatrix.set(
      at(0, 0),
      at(0, 1),
      at(0, 2),
      0,
      at(1, 0),
      at(1, 1),
      at(1, 2),
      0,
      at(2, 0),
      at(2, 1),
      at(2, 2),
      0,
      0,
      0,
      0,
      1,
    );
    mesh.quaternion.setFromRotationMatrix(scratchMatrix);
  }
}
